import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, ScanLine, LogOut, Truck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Mobile-first shell for Field Officer role.
 * No sidebar. Compact header + bottom tab bar (app-style).
 */
export default function FieldLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const isActive = (p) => loc.pathname === p || loc.pathname.startsWith(p + "/");

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 flex flex-col"
      data-testid="field-shell"
    >
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="/gasify_logo.png"
            alt="Gasify"
            className="h-7 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.25em] text-flame-400 font-semibold">Field</div>
            <div className="text-xs text-slate-300 truncate max-w-[10rem]">{user?.name || "Officer"}</div>
          </div>
        </div>
        <button
          onClick={async () => { await logout(); nav("/login"); }}
          data-testid="field-logout-btn"
          className="text-slate-400 hover:text-white p-2 rounded-full"
          aria-label="Sign out"
        >
          <LogOut size={18} />
        </button>
      </header>

      {/* Body */}
      <main className="flex-1 px-4 py-5 pb-24 max-w-md w-full mx-auto">
        <Outlet />
      </main>

      {/* Bottom tab bar */}
      <nav
        data-testid="field-tabbar"
        className="fixed bottom-0 inset-x-0 z-30 bg-slate-900/95 backdrop-blur border-t border-slate-800"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="max-w-md mx-auto grid grid-cols-3">
          <TabBtn
            label="Home"
            icon={<Home size={20} />}
            active={isActive("/field") && !isActive("/field/scan") && !isActive("/field/deliveries")}
            onClick={() => nav("/field")}
            testId="field-tab-home"
          />
          <TabBtn
            label="Scan"
            icon={<ScanLine size={20} />}
            active={isActive("/field/scan")}
            onClick={() => nav("/field/scan")}
            testId="field-tab-scan"
          />
          <TabBtn
            label="Deliveries"
            icon={<Truck size={20} />}
            active={isActive("/field/deliveries")}
            onClick={() => nav("/field/deliveries")}
            testId="field-tab-deliveries"
          />
        </div>
      </nav>
    </div>
  );
}

const TabBtn = ({ label, icon, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex flex-col items-center justify-center gap-1 py-3 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
      active ? "text-flame-400" : "text-slate-400 hover:text-slate-200"
    }`}
  >
    {icon}
    <span>{label}</span>
  </button>
);
